/**
 * TerrainPanel — Contours as real layers, 3D, LiDAR upload
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Terrain, ShowChart, ThreeDRotation, WaterDrop } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { addLayer, setActiveLayer } from '../../../store/slices/layersSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { contourService } from '../../../services/ContourService';
import { lasParser } from '../../../services/LASParser';
import { mapNavigator } from '../../../services/MapNavigator';
import type { Symbology } from '../../../types/symbology';

const contourSymbology: Symbology = {
  type: 'simple',
  strokeColor: '#c4a35a',
  strokeWidth: 1.5,
  opacity: 0.95,
  fillColor: 'transparent',
};

const TerrainPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const [contourInterval, setContourInterval] = useState(5);
  const [zField, setZField] = useState('elevation');
  const [sourceLayerId, setSourceLayerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [lasPoints, setLasPoints] = useState<any[]>([]);
  const [lasLoaded, setLasLoaded] = useState(false);

  const pointLayers = useMemo(
    () => layers.filter((l) => l.type === 'vector' && l.geometryType === 'Point'),
    [layers]
  );

  const activeSource = pointLayers.find((l) => l.id === sourceLayerId) || pointLayers[0];

  // Suggest Z fields from first point layer
  const zFieldOptions = useMemo(() => {
    const keys = new Set<string>(['elevation', 'z', 'height', 'altitude']);
    const sample = activeSource?.features?.slice(0, 20) || [];
    for (const f of sample) {
      Object.keys(f.properties || {}).forEach((k) => keys.add(k));
    }
    return Array.from(keys);
  }, [activeSource]);

  const generateContours = async () => {
    const pts = activeSource;
    if (!pts?.features || pts.features.length < 4) {
      dispatch(
        addNotification({
          type: 'warning',
          message: 'Need a Point layer with ≥4 points that have elevation (Z).',
        })
      );
      return;
    }
    setBusy(true);
    try {
      const contours = await contourService.generateFromPoints(
        pts.features as any,
        zField,
        contourInterval
      );
      if (!contours.length) {
        dispatch(
          addNotification({
            type: 'warning',
            message: 'No contours generated. Check Z field values and interval.',
          })
        );
        return;
      }

      const id = `contours-${Date.now()}`;
      const features = contours.map((f, i) => ({
        id: `${id}-f${i}`,
        layerId: id,
        geometry: f.geometry,
        properties: f.properties || {},
      }));

      const layer = {
        id,
        name: `Contours ${contourInterval}m — ${pts.name}`,
        type: 'vector' as const,
        geometryType: 'LineString' as const,
        visible: true,
        opacity: 1,
        zIndex: layers.length + 1,
        featureCount: features.length,
        features,
        symbology: contourSymbology,
        source: 'contour-service',
      };

      dispatch(addLayer(layer));
      dispatch(setActiveLayer(id));
      window.setTimeout(() => mapNavigator.zoomToLayer(id, [...layers, layer]), 150);
      dispatch(
        addNotification({
          type: 'success',
          message: `Created ${features.length} contour lines at ${contourInterval}m`,
        })
      );
    } catch (e) {
      dispatch(
        addNotification({
          type: 'error',
          message: e instanceof Error ? e.message : 'Contour generation failed',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const open3D = () => {
    window.dispatchEvent(new CustomEvent('geovera:toggle-3d', { detail: { enabled: true } }));
    dispatch(addNotification({ type: 'info', message: '3D preview opened below the map' }));
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Terrain color="success" />
        <Typography variant="subtitle2" fontWeight={700}>
          TERRAIN & 3D
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary">
        Contours · 3D preview · LiDAR points
      </Typography>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Contours from points
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Point layer</InputLabel>
        <Select
          label="Point layer"
          value={activeSource?.id || ''}
          onChange={(e) => setSourceLayerId(e.target.value)}
        >
          {pointLayers.length === 0 && (
            <MenuItem value="" disabled>
              No point layers — import XYZ points
            </MenuItem>
          )}
          {pointLayers.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name} ({l.features?.length || 0})
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
        <InputLabel>Elevation field</InputLabel>
        <Select label="Elevation field" value={zField} onChange={(e) => setZField(e.target.value)}>
          {zFieldOptions.map((f) => (
            <MenuItem key={f} value={f}>
              {f}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ minWidth: 88 }}>
          Interval: {contourInterval}m
        </Typography>
        <Slider
          size="small"
          min={0.5}
          max={50}
          step={0.5}
          value={contourInterval}
          onChange={(_, v) => setContourInterval(v as number)}
          sx={{ flex: 1 }}
        />
      </Box>

      <Button
        fullWidth
        variant="contained"
        size="small"
        startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ShowChart />}
        onClick={generateContours}
        disabled={busy || pointLayers.length === 0}
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Generate contours as layer
      </Button>

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        3D visualization
      </Typography>
      <Button
        fullWidth
        variant="outlined"
        size="small"
        startIcon={<ThreeDRotation />}
        onClick={open3D}
        sx={{ mb: 1, textTransform: 'none', fontWeight: 600 }}
      >
        Open 3D preview
      </Button>

      <input
        type="file"
        accept=".las,.laz"
        style={{ display: 'none' }}
        id="las-upload"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          try {
            const buf = await file.arrayBuffer();
            const { points } = await lasParser.parseLAS(buf, 15000);
            setLasPoints(points);
            setLasLoaded(true);
            // Optional: add sample as point layer for map context
            const id = `las-${Date.now()}`;
            const sample = points.slice(0, 2000);
            if (sample.length && sample[0].x != null) {
              const features = sample.map((p: any, i: number) => ({
                id: `${id}-f${i}`,
                layerId: id,
                geometry: {
                  type: 'Point' as const,
                  coordinates: [p.x, p.y, p.z ?? 0],
                },
                properties: {
                  z: p.z,
                  classification: p.classification,
                  source: 'las',
                },
              }));
              dispatch(
                addLayer({
                  id,
                  name: `LiDAR sample — ${file.name}`,
                  type: 'vector',
                  geometryType: 'Point',
                  visible: true,
                  opacity: 0.9,
                  zIndex: layers.length + 1,
                  featureCount: features.length,
                  features,
                })
              );
              dispatch(setActiveLayer(id));
            }
            dispatch(
              addNotification({
                type: 'success',
                message: `Loaded ${points.length} LAS points (sample on map)`,
              })
            );
          } catch {
            dispatch(
              addNotification({
                type: 'error',
                message: 'LAS parse failed — try uncompressed .las',
              })
            );
          }
        }}
      />
      <Button
        size="small"
        fullWidth
        variant="outlined"
        sx={{ textTransform: 'none' }}
        onClick={() => document.getElementById('las-upload')?.click()}
      >
        Upload .las / .laz point cloud
      </Button>
      {lasLoaded && (
        <Typography variant="caption" sx={{ color: 'success.main', display: 'block', mt: 0.5 }}>
          LAS ready — {lasPoints.length} points
        </Typography>
      )}

      <Divider sx={{ my: 1.5 }} />
      <Button size="small" fullWidth startIcon={<WaterDrop />} variant="outlined" disabled>
        Viewshed / hydrology (coming soon)
      </Button>

      <Alert severity="info" sx={{ fontSize: 11, mt: 1.5 }}>
        Contours use elevation from point attributes (or coordinate Z). Results are a new LineString
        layer you can style and export.
      </Alert>
    </Box>
  );
};

export default TerrainPanel;
